/**
 * ss-hero-v32.js — autoplay first, manual nav kept
 * - Auto-scrolls by default when 2+ slides
 * - Arrows / dots / swipe / keyboard remain
 * - Pauses on hover / touch / hidden tab; resumes cleanly
 * - Restarts after age-gate unlock
 */
(function () {
  'use strict';

  function truthy(v) {
    if (v === true || v === 1) return true;
    if (v == null) return false;
    var s = String(v).toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'yes' || s === 'on';
  }

  function initHero(root) {
    if (!root || root.dataset.ssHeroReady === '1') return;
    root.dataset.ssHeroReady = '1';

    var slides = Array.prototype.slice.call(root.querySelectorAll('.ss-hero__slide'));
    if (!slides.length) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-ss-hero-dot]'));
    var prevBtn = root.querySelector('[data-ss-hero-prev]');
    var nextBtn = root.querySelector('[data-ss-hero-next]');
    /* Default ON unless explicitly disabled in theme editor */
    var autoplayAttr = root.getAttribute('data-autoplay');
    var autoplay = autoplayAttr == null || autoplayAttr === '' ? true : truthy(autoplayAttr);
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    if (interval < 2500) interval = 2500;
    var pauseHover = root.getAttribute('data-pause-hover') !== 'false';
    var reduce = false;
    var index = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
    if (index < 0) index = 0;
    var timer = null;
    var touching = false;
    var hovering = false;
    var startX = 0;
    var userPaused = false;

    root.style.setProperty('--ss-hero-interval', interval + 'ms');
    root.setAttribute('data-autoplay', autoplay ? 'true' : 'false');

    function resetDotBars() {
      dots.forEach(function (d) {
        var bar = d.querySelector('.ss-hero__dot-bar');
        if (!bar) return;
        bar.style.transition = 'none';
        bar.style.width = '0%';
        void bar.offsetWidth;
        bar.style.removeProperty('transition');
        bar.style.removeProperty('width');
      });
    }

    function go(n, fromUser) {
      index = (n + slides.length) % slides.length;
      resetDotBars();
      slides.forEach(function (s, i) {
        var on = i === index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        var vid = s.querySelector('video');
        if (vid) {
          if (on) {
            try { vid.play(); } catch (e) {}
          } else {
            try { vid.pause(); } catch (e) {}
          }
        }
      });
      dots.forEach(function (d, i) {
        var on = i === index;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (fromUser) userPaused = false;
      restart();
    }

    function next(fromUser) { go(index + 1, fromUser); }
    function prev(fromUser) { go(index - 1, fromUser); }

    function canAutoplay() {
      if (!autoplay || reduce || slides.length < 2) return false;
      if (touching || hovering || userPaused) return false;
      if (document.hidden) return false;
      if (document.documentElement.classList.contains('ss-age-locked')) return false;
      if (document.documentElement.classList.contains('ss-age-pending') &&
          !document.documentElement.classList.contains('ss-age-verified')) return false;
      var rect = root.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > (window.innerHeight || 0) + 40) return false;
      return true;
    }

    function stop() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function restart() {
      stop();
      if (!canAutoplay()) return;
      timer = setTimeout(function () {
        next(false);
      }, interval);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        prev(true);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        next(true);
      });
    }
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        go(parseInt(d.getAttribute('data-ss-hero-dot'), 10) || 0, true);
      });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(true); }
    });
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '0');

    if (pauseHover) {
      root.addEventListener('mouseenter', function () {
        hovering = true;
        stop();
      });
      root.addEventListener('mouseleave', function () {
        hovering = false;
        restart();
      });
    }

    var frame = root.querySelector('.ss-hero__frame');
    if (frame) {
      frame.addEventListener('touchstart', function (e) {
        touching = true;
        stop();
        startX = e.changedTouches[0].clientX;
      }, { passive: true });
      frame.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        touching = false;
        if (Math.abs(dx) > 48) {
          if (dx < 0) next(true);
          else prev(true);
        } else {
          restart();
        }
      }, { passive: true });

      var dragging = false;
      var dragStart = 0;
      frame.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('a, button')) return;
        dragging = true;
        dragStart = e.clientX;
        stop();
      });
      window.addEventListener('pointerup', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.clientX - dragStart;
        if (Math.abs(dx) > 60) {
          if (dx < 0) next(true);
          else prev(true);
        } else {
          restart();
        }
      });
    }

    root.querySelectorAll('[data-ss-countdown]').forEach(function (el) {
      var end = new Date(el.getAttribute('data-ss-countdown') + 'T23:59:59');
      if (isNaN(end.getTime())) return;
      function tick() {
        var diff = Math.max(0, end - Date.now());
        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var days = el.querySelector('[data-unit="days"]');
        var hours = el.querySelector('[data-unit="hours"]');
        var mins = el.querySelector('[data-unit="mins"]');
        if (days) days.textContent = String(d).padStart(2, '0');
        if (hours) hours.textContent = String(h).padStart(2, '0');
        if (mins) mins.textContent = String(m).padStart(2, '0');
      }
      tick();
      setInterval(tick, 30000);
    });

    if (!reduce && window.matchMedia('(hover: hover)').matches) {
      root.addEventListener('mousemove', function (e) {
        var active = root.querySelector('.ss-hero__slide.is-active [data-ss-hero-parallax]');
        if (!active) return;
        var r = root.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        active.style.transform = 'translate(' + (x * 12).toFixed(1) + 'px,' + (y * 8).toFixed(1) + 'px)';
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else restart();
    });

    document.addEventListener('ss:age-verified', function () {
      hovering = false;
      touching = false;
      restart();
    });

    window.addEventListener('scroll', function () {
      if (!timer && canAutoplay()) restart();
      if (timer && !canAutoplay()) stop();
    }, { passive: true });

    go(index, false);
  }

  function boot() {
    document.querySelectorAll('[data-ss-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-ss-hero]').forEach(function (el) {
      delete el.dataset.ssHeroReady;
    });
    boot();
  });
})();
